import React from 'react';

/**
 * Landing tile icons.
 *
 * Authored as inline SVG rather than raster art so they stay sharp at any
 * density and, more importantly, so they can theme themselves: the strokes
 * are `currentColor`, which `.landing-tile__icon` sets to var(--kkUltraViolet).
 * That token remaps per brand (Kit Keeper / Keepr purple, St Anne's #B92035),
 * so one asset serves all three flows. An <img> could not do this — an
 * external SVG is an isolated document and cannot inherit the page's colour.
 *
 * Two-tone by opacity rather than by a second hue. DESIGN_LANGUAGE.md §3
 * reserves lime for sparkle, but lime is a *light* colour: #bbf351 is 1.2:1
 * on white and 1.1:1 on the --kkUltraVioletVeryLight disc these sit on, so it
 * would be invisible here. It earns its place on the dark header bar, not on
 * a white tile. A 16% wash of the brand colour gives the depth instead and
 * stays legible in every theme.
 *
 * Decorative: each tile's <h3> carries the meaning, so they are aria-hidden.
 */

interface TileIconProps {
  className?: string;
}

const SVG_PROPS = {
  viewBox: '0 0 48 48',
  fill: 'none',
  'aria-hidden': true as const,
  focusable: false as const,
};

const STROKE = {
  stroke: 'currentColor',
  strokeWidth: 2.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Archive box with a lid — storage. */
export function StorageIcon({ className }: TileIconProps): JSX.Element {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect
        x="5"
        y="9"
        width="38"
        height="10"
        rx="2.5"
        fill="currentColor"
        opacity="0.16"
      />
      <rect x="5" y="9" width="38" height="10" rx="2.5" {...STROKE} />
      <path d="M8.5 19v17.5A2.5 2.5 0 0 0 11 39h26a2.5 2.5 0 0 0 2.5-2.5V19" {...STROKE} />
      <path d="M19 27.5h10" {...STROKE} />
    </svg>
  );
}

/** Parcel with motion lines — shipping. */
export function ShippingIcon({ className }: TileIconProps): JSX.Element {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path
        d="M16 15.5A2.5 2.5 0 0 1 18.5 13h20a2.5 2.5 0 0 1 2.5 2.5V22H16v-6.5Z"
        fill="currentColor"
        opacity="0.16"
      />
      <rect x="16" y="13" width="25" height="22" rx="2.5" {...STROKE} />
      {/* Lid seam plus a tape stub across it — without the stub the shape
          reads as a card rather than a box. */}
      <path d="M16 22h25M28.5 18v8" {...STROKE} />
      <path d="M11 18H5M11 24H4M11 30H7" {...STROKE} />
    </svg>
  );
}

/** Box van — removals (Keepr flow only). */
export function RemovalsIcon({ className }: TileIconProps): JSX.Element {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect
        x="6"
        y="12"
        width="22"
        height="18"
        rx="2.5"
        fill="currentColor"
        opacity="0.16"
      />
      <rect x="6" y="12" width="22" height="18" rx="2.5" {...STROKE} />
      <path
        d="M28 18h6.4a2 2 0 0 1 1.6.8l4.4 5.9a2 2 0 0 1 .4 1.2V28a2 2 0 0 1-2 2H28"
        {...STROKE}
      />
      <circle cx="14" cy="33" r="3.5" {...STROKE} />
      <circle cx="34" cy="33" r="3.5" {...STROKE} />
    </svg>
  );
}
